// PriceSnapshotDrawer.jsx
import React, { useMemo, useState } from "react";
import CalendarPopover from "./CalenderPopover.jsx";

const DEFAULT_CURRENCIES = ["CZK", "EUR"];
const SPECIAL_SINGLE_CZK = new Set(["Kurz", "TF zboží", "TF cig"]);

function pad2(n) {
  return String(n).padStart(2, "0");
}

// "YYYY-MM-DD" + hour -> "YYYY-MM-DDTHH:00" pro datetime-local
function toLocalInputValue(dayStr, hour) {
  return `${dayStr}T${pad2(hour)}:00`;
}

function fmtTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("cs-CZ", { 
    day: "2-digit", 
    month: "2-digit", 
    year: "numeric", 
    hour: "2-digit", 
    minute: "2-digit" 
  });
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtVal(v, cur) {
  if (v == null) return "";
  return cur === "EUR" ? v.toFixed(3) : v.toFixed(2);
}

function delta(a, b) {
  const aa = toNum(a);
  const bb = toNum(b);
  if (aa == null || bb == null) return null;
  const d = bb - aa;
  if (Object.is(d, -0)) return 0;
  return d;
}

function deltaText(d, cur) {
  if (d == null) return "";
  const s = d > 0 ? `+${fmtVal(d, cur)}` : fmtVal(d, cur);
  return s;
}
function fuelToneClass(fuel) {
  const key = String(fuel || "")
    .toLowerCase()
    .replace(/\s+/g, "-");

  return `hist-fuel-card--${key}`;
}

function FuelGroup({
  fuel,
  pricesAt,
  pricesNow,
  showDelta,
  showEurSlot,
  allowEurForStationFuel,
}) {
  const currencies = useMemo(() => {
    if (SPECIAL_SINGLE_CZK.has(fuel)) return ["CZK"];
    return showEurSlot ? DEFAULT_CURRENCIES : ["CZK"];
  }, [fuel, showEurSlot]);

  return (
    <div className={`hist-fuel-card ${fuelToneClass(fuel)}`}>
      <div className="hist-fuel-card__label">{fuel}</div>

      <div className="hist-fuel-card__currencies">
        {currencies.map((cur) => {
          const eurBlocked = cur === "EUR" && !allowEurForStationFuel;

          const atV = eurBlocked ? null : pricesAt?.[fuel]?.[cur]?.value ?? null;
          const nowV = eurBlocked ? null : pricesNow?.[fuel]?.[cur]?.value ?? null;

          const isEmpty = atV == null && (!showDelta || nowV == null);

          if (isEmpty) {
            return (
              <div
                key={`${fuel}-${cur}`}
                className="hist-price-cell hist-price-cell--empty"
              />
            );
          }

          const d = showDelta ? delta(atV, nowV) : null;

          return (
            <div key={`${fuel}-${cur}`} className="hist-price-cell">
              <small className="hist-price-cell__currency">{cur}</small>

              <input
                type="text"
                readOnly
                value={atV == null ? "" : fmtVal(Number(atV), cur)}
                className="hist-price-cell__input"
              />

              {showDelta && (
                <div
                  className={[
                    "hist-price-cell__delta",
                    d == null ? "" : d === 0 ? "is-zero" : d > 0 ? "is-plus" : "is-minus",
                  ].join(" ")}
                >
                  {deltaText(d, cur)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function unionInOrder(a = [], b = []) {
  const out = [];
  const seen = new Set();
  for (const x of a) {
    if (!seen.has(x)) { seen.add(x); out.push(x); }
  }
  for (const x of b) {
    if (!seen.has(x)) { seen.add(x); out.push(x); }
  }
  return out;
}

export default function PriceSnapshotDrawer({
  open,
  onClose,
  armexNow,
  nearbyNow,
  snapshot,       
  onLoadSnapshot, 
  snapAt,
  setSnapAt,
  loading,
  compareNow,
  setCompareNow,
  eurAllowedFuels,          
  eurFuelsNow,              
  eurFuelsSnapshot,         
  fuelOrder = [],
  historySummary,
  historySummaryLoading,
}) {
  const [selectedDay, setSelectedDay] = React.useState(null);
  const [calOpen, setCalOpen] = useState(false);

  const days = historySummary?.days || [];
  const hoursByDay = historySummary?.hoursByDay || {};
  const selectedHours = selectedDay ? (hoursByDay[selectedDay] || []) : [];

  const eurFuelsView = useMemo(() => {
    const out = new Set();

    const add = (x) => {
      if (!x) return;
      if (x instanceof Set) for (const v of x) out.add(v);
      else if (Array.isArray(x)) for (const v of x) out.add(v);
    };

    add(eurFuelsNow);
    add(eurFuelsSnapshot);

    out.delete("Kurz");
    out.delete("TF zboží");
    out.delete("TF cig");

    return out;
  }, [eurFuelsNow, eurFuelsSnapshot]);

  const isEurAllowedForStationFuel = (station, fuel) => {
    if (!station) return false;
    const hasEUR = String(station?.display_euro) === "1" || station?.display_euro === 1;
    if (!hasEUR) return false;
    return !!(eurAllowedFuels && eurAllowedFuels.has && eurAllowedFuels.has(fuel));
  };

  const dayCountMap = useMemo(() => {
    const m = new Map();
    for (const d of (historySummary?.days || [])) m.set(d.date, d.count);
    return m;
  }, [historySummary]);

  const hoursMap = useMemo(() => {
    // day -> Map(hour -> count)
    const out = new Map();
    const src = historySummary?.hoursByDay || {};
    for (const day of Object.keys(src)) {
      const hm = new Map();
      for (const h of (src[day] || [])) hm.set(h.hour, h.count);
      out.set(day, hm);
    }
    return out;
  }, [historySummary]);

  const selectedHoursMap = useMemo(() => {
    if (!selectedDay) return new Map();
    return hoursMap.get(selectedDay) || new Map();
  }, [hoursMap, selectedDay]);

  const fuels = useMemo(() => {
    const nowKeys = Object.keys(armexNow?.prices || {});
    const atKeys  = Object.keys(snapshot?.armex?.prices || {});

    const present = new Set([...nowKeys, ...atKeys]);

    const order = Array.isArray(fuelOrder) ? fuelOrder : [];

    const ordered = order.filter((f) => present.has(f));

    const rest = unionInOrder(nowKeys, atKeys).filter((f) => !ordered.includes(f));

    return [...ordered, ...rest];
  }, [armexNow?.prices, snapshot?.armex?.prices, fuelOrder]);

  React.useEffect(() => {
    if (!open) return;
    const arr = historySummary?.days || [];
    if (!arr.length) return;
    setSelectedDay(arr[arr.length - 1].date);
  }, [open, historySummary]);

  if (!open) return null;

  return (
    <div className="hist-overlay" onMouseDown={onClose}>
      <div className="hist-drawer snap-drawer" onMouseDown={(e) => e.stopPropagation()}>
        <div className="hist-header">
          <div>
            <div className="hist-title">Stav cen k datu</div>
            <div className="hist-subtitle">
              {armexNow?.name} <span className="hist-muted">({armexNow?.id})</span>
              {snapshot?.atIso && (
                <span className="hist-muted"> · {fmtTs(snapshot.atIso)}</span>
              )}
            </div>
          </div>
          <button className="hist-close" onClick={onClose} aria-label="Zavřít">✕</button>
        </div>

        <div className="snap-controls">
          <div className="snap-dt-row">
            <label>Den</label>

            <div className="snap-dt-inputwrap">
              <input
                type="text"
                readOnly
                value={selectedDay || ""}
                placeholder={historySummaryLoading ? "Načítám…" : "Vyber den"}
                onClick={() => setCalOpen(true)}
                className="snap-input"
              />
              <button 
                type="button" 
                className="snap-cal-btn" 
                onClick={() => setCalOpen(true)} 
                aria-label="Vybrat den"
              >
                📅
              </button>
            </div>

            <label>Ručně</label>
            <input
              type="datetime-local"
              value={snapAt}
              onChange={(e) => setSnapAt(e.target.value)}
              className="snap-input snap-input--dt"
            />

            <button
              type="button"
              className="confirm-button-hitory"
              onClick={() => {
                if (!snapAt) return;
                const atIso = new Date(snapAt).toISOString();
                onLoadSnapshot?.(atIso);
              }}
              disabled={loading || !snapAt}
            >
              Načíst
            </button>

            <label className="snap-toggle">
              <input
                type="checkbox"
                checked={compareNow}
                onChange={(e) => setCompareNow(e.target.checked)}
              />
              Porovnat s aktuálem (Δ)
            </label>
          </div>

          <div className="snap-hours-grid">
            {Array.from({ length: 24 }).map((_, hour) => {
              const cnt = selectedHoursMap.get(hour) || 0;
              const enabled = cnt > 0;

              return (
                <button
                  key={hour}
                  type="button"
                  className={[
                    "snap-hour2",
                    enabled ? "has-changes" : "is-empty",
                  ].join(" ")}
                  disabled={!enabled}
                  onClick={() => {
                    if (!selectedDay) return;
                    setSnapAt(toLocalInputValue(selectedDay, hour));
                  }}
                  title={enabled ? `${cnt} změn` : "bez změn"}
                >
                  {pad2(hour)}:00
                  {enabled && <span className="snap-hour2-badge">{cnt}</span>}
                </button>
              );
            })}
          </div>

          <CalendarPopover
            open={calOpen}
            onClose={() => setCalOpen(false)}
            selectedDay={selectedDay}
            onPickDay={(d) => {
              setSelectedDay(d);

              const hm = hoursMap.get(d) || new Map();
              let bestHour = null;
              let bestCnt = -1;
              for (const [h, c] of hm.entries()) {
                if (c > bestCnt) { bestCnt = c; bestHour = h; }
              }
              if (bestHour != null) setSnapAt(toLocalInputValue(d, bestHour));
              else setSnapAt(`${d}T00:00`);
            }}
            dayCountMap={dayCountMap}
            rangeFromIso={historySummary?.range?.from}
            rangeToIso={historySummary?.range?.to}
          />
        </div>

        <div className="snap-body">
          {loading ? (
            <div className="hist-loading">Načítám snapshot…</div>
          ) : !snapshot ? (
            <div className="hist-empty">Vyber čas a klikni Načíst.</div>
          ) : (
            <>
              <div className="snap-section">
                <div className="snap-section-title">Armex {armexNow?.name}</div>
                    <div className="hist-station-row">
                      <div className="hist-price-grid">
                    {fuels.map((fuel) => (
                      <FuelGroup
                        key={fuel}
                        fuel={fuel}
                        pricesAt={snapshot.armex?.prices}
                        pricesNow={armexNow?.prices}
                        showDelta={compareNow}
                        showEurSlot={eurFuelsView.has(fuel)}                        
                        allowEurForStationFuel={isEurAllowedForStationFuel(snapshot.armex ?? armexNow, fuel)} 
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="snap-section">
                  <div className="snap-section-title">Okolní ČS</div>

                  {snapshot.nearby?.map((st) => {
                    const nowStation = (nearbyNow || []).find((x) => x.id === st.id);

                    return (
                      <div key={st.id} className="hist-station-card">
                        <strong className="hist-station-card__name">{st.name}</strong>

                        <div className="hist-price-grid">
                          {fuels
                            .filter((fuel) => fuel !== "TF zboží" && fuel !== "TF cig")
                            .map((fuel) => (
                              <FuelGroup
                                key={`${st.id}-${fuel}`}
                                fuel={fuel}
                                pricesAt={st.prices}
                                pricesNow={nowStation?.prices}
                                showDelta={compareNow}
                                showEurSlot={eurFuelsView.has(fuel)}
                                allowEurForStationFuel={isEurAllowedForStationFuel(st ?? nowStation, fuel)}
                              />
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}